import { useMutation } from "@apollo/client/react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Head } from "../../../../../components/core";
import {
  NotifyType,
  useNotifyContext,
} from "../../../../../contexts/NotifyContext";
import {
  ADD_FUELENTRY,
  GET_FUELENTRIES,
  VehicleFuelEntryForm,
} from "../../../../../graphql/fleets/vehicles/pages/fuel-entry";
import Form from "../../components/FuelEntryForm";

const FuelEntryCreate = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => {
  const { t } = useTranslation();
  const { addNotify } = useNotifyContext();
  const navigate = useNavigate();

  const [createVehicleFuelEntry] = useMutation(ADD_FUELENTRY, {
    refetchQueries: [{ query: GET_FUELENTRIES }],
  });
  const handleCreate = (
    values: VehicleFuelEntryForm,
    actions: { setSubmitting: (arg0: boolean) => void }
  ) => {
    createVehicleFuelEntry({
      variables: {
        vehicleId: values.vehicleId,
        vehicleFuelTypeId: values.vehicleFuelTypeId,
        referenceNo: values.referenceNo,
        pricePerUnit: values.pricePerUnit,
        odometer: values.odometer,
        fuelFlags: values.fuelFlags,
        fuelEntryDate: values.fuelEntryDate,
        fuelAmount: values.fuelAmount,
        fleetVendorId: values.fleetVendorId,
        files: values.files,
        comments: values.comments,
      },
    })
      .then(async ({ data }) => {
        if (data?.vehicleFuelEntryCreate) {
          return navigate(
            `/fleets/vehicles/fuel-entries/${data.vehicleFuelEntryCreate.vehicleFuelEntry.id}`
          );
        } else {
          toast.error("Something went wrong, please try again later");
        }
        actions.setSubmitting(false);
      })
      .catch((error) => {
        actions.setSubmitting(false);
        addNotify({
          type: NotifyType.ERROR,
          title: "Fuel entry create failed",
          message: error.message,
        });
      });
  };

  return (
    <>
      <Head
        title={FuelEntryCreateResource.name}
        heading={FuelEntryCreateResource.name}
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: FuelEntryCreateResource.name,
            href: null,
          },
        ]}
      />
      <div className="max-w-medium mx-auto">
        <div className="rounded-xl bg-greyish p-5">
          <div className="mb-6 sm:flex sm:items-center md:mb-8">
            <div className="sm:flex-auto">
              <h1 className="text-xl font-medium text-gray-900">
                {t("heading_vehicle_fuel_entry_create")}
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                {t("description_vehicle_fuel_entry_create")}
              </p>
            </div>
          </div>

          <Form
            initialValues={{
              vehicleId: null,
              vehicleFuelTypeId: null,
              referenceNo: "",
              pricePerUnit: null,
              odometer: null,
              fuelFlags: null,
              fuelEntryDate: null,
              fuelAmount: null,
              fleetVendorId: null,
              files: [],
              comments: "",
            }}
            onSubmit={handleCreate}
            submitLabel={t("text_create")}
            onCancel={() => {
              navigate("/fleets/vehicles/fuel-entries");
            }}
            vehicleBasic={null}
          />
        </div>
      </div>
    </>
  );
};

export default FuelEntryCreate;
export const FuelEntryCreateResource: ResourceProps = {
  name: "Fuel Entry",
  description: "Update an existing vehicle fuel entries",
  access: ["update-vehicles"],
  path: "/fuel-entries/add",
  icon: "bi bi-person",
};
